import { AvailabilityBoard } from "@/components/availability/AvailabilityBoard";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { useRecentlyChangedRowsApi } from "@/contexts/RecentlyChangedRowsContext";
import type { Assignment, ProjectSummary } from "@/hooks/useProjectBoardData";
import { useMakersBoardData } from "@/hooks/useMakersBoardData";
import { useRoles } from "@/hooks/useRoles";
import { clampAvailabilityDockHeight } from "@/utils/availabilityDockUtils";
import {
  employeeSummaryToAssignmentEmployee,
  saveAssignmentEmployees,
} from "@/utils/assignmentEmployees";
import { withWrittenEmployees } from "@/utils/assignmentConfirmation";
import { formatEmployeeDisplayName } from "@/utils/nameUtils";
import { assignmentAuditLabel, projectAuditLabel } from "@/utils/auditLabels";
import { addWeeks, parseISODate, toISODateString } from "@/utils/dateUtils";
import { requiredHoursByWeekForFocus } from "@/utils/projectAvailabilityUtils";
import { formatProjectDisplayName } from "@/utils/projectUtils";
import { getStartOfWeek } from "@/utils/weekUtils";
import { CalendarRange, X } from "lucide-react";
import type { PointerEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface ProjectAvailabilityFocus {
  project: ProjectSummary;
  assignment: Assignment;
}

interface ProjectAvailabilityDockProps {
  /** Where the board is currently scrolled; seeds the dock's range on open. */
  timelineStartDate: string;
  focus: ProjectAvailabilityFocus | null;
  heightPx: number;
  onHeightChange: (heightPx: number) => void;
  updateProjectOptimistically: (
    projectId: string,
    updates: Partial<ProjectSummary>
  ) => void;
  refreshSilently: () => Promise<void>;
  onClearFocus: () => void;
  onClose: () => void;
}

const DEFAULT_AVAILABILITY_WEEKS = 13;

function endDateForRange(startDate: string, weeks: number): string {
  return toISODateString(addWeeks(parseISODate(startDate), Math.max(1, weeks) - 1));
}

export function ProjectAvailabilityDock({
  timelineStartDate,
  focus,
  heightPx,
  onHeightChange,
  updateProjectOptimistically,
  refreshSilently,
  onClearFocus,
  onClose,
}: ProjectAvailabilityDockProps) {
  const { employees, loading, error } = useMakersBoardData();
  const { formatRole } = useRoles();
  const { markSelfEdit } = useRecentlyChangedRowsApi();

  const [startDate, setStartDate] = useState(timelineStartDate);
  const [endDate, setEndDate] = useState(() =>
    endDateForRange(timelineStartDate, DEFAULT_AVAILABILITY_WEEKS),
  );
  const resizeDragRef = useRef<{ startY: number; startHeight: number } | null>(null);

  const focusKey = focus ? `${focus.project.id}:${focus.assignment.id}` : "global";
  const focusProjectStartDate = focus?.project.start_date ?? null;
  const focusProjectNumWeeks = focus?.project.num_weeks ?? null;

  useEffect(() => {
    if (focusProjectStartDate == null || focusProjectNumWeeks == null) return;

    const focusedProjectStartWeek = toISODateString(getStartOfWeek(parseISODate(focusProjectStartDate)));
    setStartDate(focusedProjectStartWeek);
    setEndDate(endDateForRange(focusedProjectStartWeek, focusProjectNumWeeks));
  }, [focusKey, focusProjectStartDate, focusProjectNumWeeks]);

  const requiredHoursByWeek = useMemo(() => {
    if (!focus) return undefined;
    return requiredHoursByWeekForFocus(focus);
  }, [focus]);

  const selectedRoles = useMemo(() => {
    if (!focus?.assignment.required_role_id) return undefined;
    return [focus.assignment.required_role_id];
  }, [focus]);

  const title = focus?.assignment.required_role_id
    ? `Availability for ${formatRole(focus.assignment.required_role_id)}`
    : "Availability";

  const subtitle = focus
    ? formatProjectDisplayName(focus.project.name, focus.project.client)
    : null;

  const handleResizePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);
      resizeDragRef.current = {
        startY: event.clientY,
        startHeight: heightPx,
      };
    },
    [heightPx]
  );

  const handleResizePointerMove = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      const dragStart = resizeDragRef.current;
      if (!dragStart) return;

      onHeightChange(clampAvailabilityDockHeight(dragStart.startHeight + dragStart.startY - event.clientY));
    },
    [onHeightChange]
  );

  const finishResizeDrag = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    resizeDragRef.current = null;
  }, []);

  const handleEmployeeClick = useCallback((employeeId: string) => {
    if (!focus) return;

    const employee = employees.find((emp) => emp.id === employeeId);
    if (!employee) return;

    const nextEmployees = [employeeSummaryToAssignmentEmployee(employee)];
    const nameById = new Map<string, string>();
    for (const assignmentEmployee of focus.assignment.employees) {
      const displayName = formatEmployeeDisplayName(assignmentEmployee);
      if (displayName) nameById.set(assignmentEmployee.id, displayName);
    }
    const displayName = formatEmployeeDisplayName(employee);
    if (displayName) nameById.set(employee.id, displayName);

    updateProjectOptimistically(focus.project.id, {
      project_assignments: focus.project.project_assignments.map((assignment) =>
        assignment.id === focus.assignment.id
          ? withWrittenEmployees(assignment, nextEmployees)
          : assignment
      ),
    });

    onClose();
    void saveAssignmentEmployees(
      focus.assignment,
      new Set([employeeId]),
      markSelfEdit,
      () => {
        void refreshSilently();
      },
      () => {
        void refreshSilently();
      },
      {
        recordLabel: assignmentAuditLabel(projectAuditLabel(focus.project), focus.assignment, formatRole),
        nameById,
        officeId: focus.project.office_id ?? null,
      },
    );
  }, [employees, focus, formatRole, markSelfEdit, onClose, refreshSilently, updateProjectOptimistically]);

  return (
    <aside className="project-availability-dock" aria-label="Availability">
      <div
        className="project-availability-dock__resize-handle"
        aria-hidden="true"
        onPointerDown={handleResizePointerDown}
        onPointerMove={handleResizePointerMove}
        onPointerUp={finishResizeDrag}
        onPointerCancel={finishResizeDrag}
      />
      <div className="project-availability-dock__header">
        <div className="project-availability-dock__title">
          <CalendarRange size={18} aria-hidden="true" />
          <span>{title}</span>
          {subtitle ? (
            <span className="project-availability-dock__subtitle">{subtitle}</span>
          ) : null}
        </div>
        {focus ? (
          <button
            type="button"
            className="project-availability-dock__clear-focus"
            onClick={onClearFocus}
          >
            Show all
          </button>
        ) : null}
        <button
          type="button"
          className="board-controls__icon-button"
          onClick={onClose}
          title="Close Availability"
          aria-label="Close Availability"
        >
          <X size={18} />
        </button>
      </div>
      <div className="project-availability-dock__body">
        {error ? <ErrorBanner message={error} entityName="employees" /> : null}
        {loading ? (
          <LoadingState message="Loading availability..." />
        ) : (
          <AvailabilityBoard
            employees={employees}
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            defaultMinAvailableHours={requiredHoursByWeek ? 0 : undefined}
            minAvailableHoursByWeek={requiredHoursByWeek}
            showMinHoursControl={!requiredHoursByWeek}
            defaultSelectedRoles={selectedRoles}
            resetFiltersKey={focusKey}
            compact
            onEmployeeClick={focus ? handleEmployeeClick : undefined}
          />
        )}
      </div>
    </aside>
  );
}
